/**
 * What the user reads when something is refused.
 *
 * The API's own `message` is written in English for the developer reading a log; this is the
 * Thai sentence for the person at the screen. The two are different jobs and it was a mistake
 * to let one do the other — a Thai fit-out team was being shown "This document has already
 * been issued" (defect #12).
 *
 * Two rules make this hold up as codes are added:
 *
 * 1. **An unknown code never falls back to the API's English.** It falls back to a Thai
 *    sentence plus the code, so the user gets something they can read and something they can
 *    quote to whoever they ask for help.
 * 2. **Copy says what to do next**, not only what went wrong. Spec 12's no-dead-ends rule is
 *    about failures as much as about empty screens.
 */
export const ERROR_COPY: Record<string, string> = {
  /* ── auth ─────────────────────────────────────────────────────────── */
  invalid_credentials: 'อีเมลหรือรหัสผ่านไม่ถูกต้อง',
  unauthorized: 'เซสชันหมดอายุ — กรุณาเข้าสู่ระบบใหม่',
  rate_limited: 'ลองเข้าสู่ระบบถี่เกินไป รอสักครู่แล้วลองใหม่',
  membership_inactive: 'บัญชีนี้ยังไม่ได้เปิดใช้งานในระบบ — ติดต่อผู้ดูแลระบบ',
  forbidden: 'บัญชีของคุณไม่มีสิทธิ์ทำรายการนี้',

  /* ── generic ──────────────────────────────────────────────────────── */
  not_found: 'ไม่พบรายการนี้ — อาจถูกลบไปแล้ว หรือคุณไม่มีสิทธิ์เห็น',
  validation_failed: 'ข้อมูลที่กรอกยังไม่ครบหรือไม่ถูกรูปแบบ',
  bad_request: 'ข้อมูลที่ส่งมาไม่ถูกต้อง',
  conflict: 'มีคนแก้รายการนี้ก่อนหน้า กรุณารีเฟรชแล้วลองใหม่',
  stale_write: 'มีคนแก้รายการนี้ก่อนหน้า กรุณารีเฟรชแล้วลองใหม่',
  internal_error: 'ระบบขัดข้อง กรุณาลองใหม่อีกครั้ง',

  /* ── google sign-in ───────────────────────────────────────────────── */
  google_not_configured: 'ยังไม่ได้ตั้งค่าการเข้าสู่ระบบด้วย Google — ใช้อีเมลกับรหัสผ่านไปก่อน',
  google_denied: 'ยกเลิกการเข้าสู่ระบบด้วย Google แล้ว',
  google_state_mismatch:
    'ลิงก์เข้าสู่ระบบหมดอายุหรือถูกเปิดข้ามอุปกรณ์ — กดเข้าสู่ระบบด้วย Google ใหม่อีกครั้ง',
  google_email_unverified:
    'Google ยังไม่ได้ยืนยันอีเมลของบัญชีนี้ — ยืนยันอีเมลใน Google ก่อนแล้วลองใหม่',
  google_no_account:
    'อีเมลนี้ยังไม่มีสิทธิ์ใช้งานระบบ — ระบบนี้ใช้ภายในบริษัท ต้องให้ผู้ดูแลเพิ่มบัญชีให้ก่อน',
  google_failed: 'เชื่อมต่อกับ Google ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',

  /* ── roles and admin ──────────────────────────────────────────────── */
  system_role_locked:
    'บทบาทระบบต้องมีสิทธิ์ผู้ดูแลเสมอ — ถ้าต้องการจำกัดสิทธิ์ ให้สร้างบทบาทใหม่แทน',
  last_admin_role:
    'ต้องเหลืออย่างน้อยหนึ่งบทบาทที่มีสิทธิ์ผู้ดูแล — ให้สิทธิ์บทบาทอื่นก่อนแล้วค่อยปิดอันนี้',

  /* ── pipeline and approvals ───────────────────────────────────────── */
  approval_required: 'สเตปนี้ต้องผ่านการอนุมัติก่อนจึงจะเลื่อนต่อได้',
  already_there: 'งานอยู่ที่สเตปนี้อยู่แล้ว',
  REVISION_LIMIT: 'ใช้สิทธิ์แก้แบบครบตามสัญญาแล้ว — รอบถัดไปต้องออกใบสั่งงานเพิ่ม',
  INSPECTION_OPEN: 'ยังมีข้อตรวจงานที่ค้างอยู่ — ต้องปิดให้ครบก่อนส่งมอบ',
  self_approval: 'ขออนุมัติเองแล้วอนุมัติเองไม่ได้ — ต้องให้คนอื่นเป็นผู้อนุมัติ',
  already_decided: 'คำขอนี้ถูกตัดสินไปแล้ว',
  note_required: 'การไม่อนุมัติต้องระบุเหตุผลเสมอ',

  /* ── documents ────────────────────────────────────────────────────── */
  document_locked: 'เอกสารออกเลขแล้ว แก้ไม่ได้ — ใช้ "ออกฉบับแก้ไข" แทน',
  invalid_transition: 'เปลี่ยนสถานะเอกสารแบบนี้ไม่ได้',
  negative_total: 'ส่วนลดมากกว่ายอดรวม — ยอดเอกสารติดลบไม่ได้',
  bad_number_pattern: 'รูปแบบเลขที่เอกสารไม่ถูกต้อง — แก้ได้ที่หน้าตั้งค่าเอกสาร',
  number_allocation_failed: 'ออกเลขเอกสารไม่สำเร็จ กรุณาลองใหม่',
  no_template: 'ยังไม่มีแบบฟอร์มสำหรับเอกสารประเภทนี้',
  pdf_renderer_unavailable: 'ยังตั้งค่าตัวสร้าง PDF ไม่เสร็จ — ติดต่อผู้ดูแลระบบ',
  pdf_render_failed: 'สร้าง PDF ไม่สำเร็จ กรุณาลองใหม่',
  pdf_unavailable: 'ยังตั้งค่าลิงก์ PDF ไม่เสร็จ — ติดต่อผู้ดูแลระบบ',
  link_expired: 'ลิงก์หมดอายุแล้ว — กดเปิดใหม่จากหน้าเอกสาร',
  bad_signature: 'ลิงก์นี้ใช้ไม่ได้ — กดเปิดใหม่จากหน้าเอกสาร',

  /* ── money ────────────────────────────────────────────────────────── */
  already_invoiced: 'งวดนี้ออกใบแจ้งหนี้ไปแล้ว',
  instalment_invoiced: 'แก้สัดส่วนไม่ได้แล้ว เพราะมีงวดที่ออกใบแจ้งหนี้ไปแล้ว',
  overpayment: 'ยอดที่กรอกมากกว่ายอดที่ค้างอยู่',
  split_not_100: 'สัดส่วนงวดต้องรวมกันได้ 100%',
  empty_split: 'สัญญาต้องมีอย่างน้อยหนึ่งงวด',
  no_contract: 'งานนี้ยังไม่มีสัญญาที่ออกแล้ว — ออกสัญญาก่อน',
  invalid_custody: 'เปลี่ยนสถานะเช็คแบบนี้ไม่ได้ — สถานะการถือเช็คเดินทางเดียว',

  /* ── cost and procurement ─────────────────────────────────────────── */
  duplicate_invoice_number: 'เลขที่ใบแจ้งหนี้นี้ของผู้ขายรายนี้ถูกบันทึกไปแล้ว',
  budget_mismatch: 'ผลรวมงบของกิจกรรมต้องเท่ากับงบรวมของงาน (BAC)',
  code_allocation_conflict: 'ออกเลขเอกสารไม่สำเร็จ กรุณาลองใหม่',

  /* ── schedule ─────────────────────────────────────────────────────── */
  SCHEDULE_CYCLE: 'ความสัมพันธ์ของกิจกรรมวนกลับมาหาตัวเอง — แก้ลำดับก่อนบันทึก',

  /* ── field capture ────────────────────────────────────────────────── */
  site_report_exists: 'มีรายงานของวันนี้อยู่แล้ว — เปิดของเดิมเพื่อแก้ไข',
  nothing_to_recheck: 'ไม่มีข้อค้างให้ตรวจซ้ำ',
  no_inspection: 'ยังไม่มีรอบตรวจงานก่อนหน้า',
  file_too_large: 'ไฟล์ใหญ่เกินกำหนด',
  unsupported_type: 'ไม่รองรับไฟล์ชนิดนี้',

  /* ── reporting ────────────────────────────────────────────────────── */
  invalid_dimension: 'มิติที่เลือกไม่ถูกต้อง',
  invalid_measure: 'ค่าที่เลือกวัดไม่ถูกต้อง',
};

/**
 * The Thai sentence for an error, always. `details.code` wins over the top-level code because
 * services use it to say what specifically went wrong inside a generic 400.
 */
export function errorMessage(error: unknown): string {
  if (!error) return '';

  const e = error as {
    code?: string;
    message?: string;
    details?: { code?: string };
    status?: number;
  };
  const code = e.details?.code ?? e.code ?? '';

  if (ERROR_COPY[code]) return ERROR_COPY[code];

  /*
   * Deliberately NOT `e.message`. The API's message is English written for a log, and showing
   * it here is how a Thai team ended up reading "This document has already been issued".
   * The code goes in the parentheses so the person can quote it when they ask for help.
   */
  if (e.status === 401) return ERROR_COPY.unauthorized!;
  if (e.status === 403) return ERROR_COPY.forbidden!;
  if (e.status === 404) return ERROR_COPY.not_found!;

  return code
    ? `ทำรายการไม่สำเร็จ กรุณาลองใหม่ หรือแจ้งผู้ดูแลระบบ (${code})`
    : 'ทำรายการไม่สำเร็จ กรุณาลองใหม่';
}
